import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Page,
  Table,
  type Column,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  FilterField,
  FilterPanel,
  FormModal,
  Input,
  Select,
  Tabs,
  Text,
} from "@marekl/adminer/components";
import {
  useConfirm,
  useDebouncedSearchFilter,
  useTableSort,
  useUrlFilters,
} from "@marekl/adminer/hooks";
import { formatDate, pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useMe, readsTeamOnly, hasAnyGlobalRole, administersAnyFederation } from "../../../auth/queries";
import {
  useTeams,
  useApproveTeam,
  useSuspendTeam,
  useAssignTeamCmfId,
  useCreateTeam,
  type Team,
} from "../../../lib/queries/teams";
import {
  federationLabel,
  useFederations,
  type Federation,
} from "../../../lib/queries/federations";
import { appliedFlowBadge, type StateBadge } from "../../../lib/memberState";
import { BadgeRow } from "../../../components/BadgeRow";
import { RowLink } from "../../../components/RowLink";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import { useSeason } from "../../../season";
import {
  MEMBERSHIP_OPTIONS,
  OUTSTANDING_OPTIONS,
  TEAM_VIEWS,
  filterTeams,
  membershipOf,
  filtersInEffect,
  outstandingApplies,
  outstandingOf,
  parseMembership,
  parseOutstanding,
  parseView,
  viewCount,
  viewOf,
  type TeamView,
} from "./teamViews";

// What the „Členství" cell says about a team. On „Aktivní" it is the season
// membership; on „Všechny" the tab the team would sit in stands in for it,
// since a pending or suspended club has no membership to speak of.
function membershipBadge(t: Team): StateBadge {
  switch (viewOf(t)) {
    case "ke-schvaleni":
      return { label: "Čeká na schválení", variant: "warn" };
    case "pozastavene":
      return { label: "Pozastavený", variant: "muted" };
    case "bez-registrace":
      return { label: "Chybí registrace", variant: "err" };
    case "aktivni":
      switch (membershipOf(t)) {
        case "registrovani":
          return { label: "Registrovaný", variant: "ok" };
        case "poharovky":
          return { label: "Jen pohárovky", variant: "muted" };
        case "zahranicni":
          return { label: "Zahraniční tým", variant: "muted" };
      }
  }
}

const OUTSTANDING_LABEL: Record<string, string> = {
  nezaplaceno: "Nezaplaceno",
  "chybi-foto": "Chybí foto",
};

// A country name for the ISO code a foreign club carries; flags arrive
// with the competition surfaces that actually need them.
function countryName(code: string): string {
  return COUNTRY_OPTIONS.find((c) => c.value === code)?.label ?? code;
}

// The wire carries the federation flattened onto the team, so it is put
// back into the shape federationLabel reads.
function labelOf(t: Team): { name: string; short_name?: string } {
  return { name: t.federation_name ?? "", short_name: t.federation_short_name };
}

function leaderName(t: Team): string | null {
  const p = t.leader?.person;
  return p ? `${p.first_name} ${p.last_name}` : null;
}

export function TeamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const federationFilter = searchParams.get("federation") ?? "";
  const { data: teams = [], isLoading, error, refetch } = useTeams(
    federationFilter ? { federation_id: federationFilter } : {},
  );
  const { data: federations = [] } = useFederations();
  const { activeSeason } = useSeason();
  const { data: me } = useMe();
  // Čtení členské základny lists every team and changes none: per row it
  // keeps „Detail" alone, and „Nový tým" is for whoever administers teams.
  const mayCreateTeam =
    hasAnyGlobalRole(me, "admin", "members_admin") || administersAnyFederation(me);

  // Searching happens HERE, not on the server: this list is deliberately
  // unpaginated (every team, always), so the rows are already in memory and
  // a round trip per keystroke would buy nothing but latency. The tab, the
  // filters and the search all live in the URL, so a narrowed list is a link
  // somebody can send, which is what the dashboard's alerts do.
  // `paginationKey` is required by the hook and inert here: the list is not
  // paginated, so there is no page param for a filter change to reset.
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });
  const q = searchParams.get("q") ?? "";
  const { draft, setDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => setFilter("q", next || null),
  });
  const view = parseView(searchParams.get("pohled"));
  const outstanding = parseOutstanding(searchParams.get("k-doreseni"));
  const membership = parseMembership(searchParams.get("clenstvi"));
  const filters = useMemo(() => ({ q, outstanding, membership }), [q, outstanding, membership]);

  const rows = useMemo(() => filterTeams(teams, view, filters), [teams, view, filters]);
  const tabs = useMemo(
    () =>
      TEAM_VIEWS.map((v) => ({
        id: v.id,
        label: v.label,
        count: viewCount(teams, v.id, filters),
        // The one tab that is a queue of work; the other counts only inform.
        countVariant: v.id === "ke-schvaleni" ? ("warn" as const) : undefined,
      })),
    [teams, filters],
  );
  // The membership options count within „Aktivní" under the other filters,
  // so each one says how many rows picking it would leave.
  const membershipCounts = useMemo(() => {
    const active = filterTeams(teams, "aktivni", { ...filters, membership: null });
    return Object.fromEntries(
      MEMBERSHIP_OPTIONS.map((m) => [m.id, active.filter((t) => membershipOf(t) === m.id).length]),
    );
  }, [teams, filters]);

  // Only mutateAsync is needed: the confirm dialog owns the pending and error
  // state these two used to render themselves. Destructured because the
  // callbacks below are dependencies of a memoised column list.
  const { mutateAsync: approveTeam } = useApproveTeam();
  const { mutateAsync: assignCmfId } = useAssignTeamCmfId();
  const suspend = useSuspendTeam();
  const createTeam = useCreateTeam();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    country_code: "CZ",
    federation_id: "",
  });
  // Suspending is the one dialog here that is not a plain question: it carries
  // a reason field, so it keeps its own open state and uses ConfirmDialog.
  const [suspending, setSuspending] = useState<Team | null>(null);
  const [reason, setReason] = useState("");
  const closeSuspend = () => {
    setSuspending(null);
    setReason("");
    suspend.reset();
  };

  // useCallback because the columns are memoised for useTableSort: an identity
  // that changes every render would re-sort on every render. `mutateAsync` is a
  // stable reference, so these survive a mutation changing state.
  const askApprove = useCallback(
    (t: Team) =>
      confirm({
        title: t.status === "suspended" ? "Aktivovat tým" : "Schválit tým",
        description: (
          <>
            Opravdu chcete {t.status === "suspended" ? "aktivovat" : "schválit"} tým{" "}
            <strong>{t.name}</strong>?
          </>
        ),
        confirmLabel: t.status === "suspended" ? "Aktivovat" : "Schválit",
        onConfirm: async () => {
          await approveTeam(t.id);
        },
      }),
    [confirm, approveTeam],
  );

  const askAssignCmfId = useCallback(
    (t: Team) =>
      confirm({
        title: "Přidělit ČNSMS ID",
        description:
          t.cmf_id == null ? (
            <>
              Týmu <strong>{t.name}</strong> bude přidělen blok ČNSMS ID (1000 čísel).
              Současně budou doplněna ID všem evidovaným členům bez vlastního ČNSMS ID.
            </>
          ) : (
            <>
              Tým <strong>{t.name}</strong> už má blok ČNSMS ID
              <strong> {t.cmf_id}</strong>. Doplníme ČNSMS ID členům, kteří ho zatím
              nemají (<strong>{t.members_missing_cmf_id ?? 0}</strong>).
            </>
          ),
        confirmLabel: "Přidělit",
        onConfirm: async () => {
          await assignCmfId(t.id);
        },
      }),
    [confirm, assignCmfId],
  );

  const pendingView = view === "ke-schvaleni";
  const seasonCode = activeSeason?.code;

  // Hoisted: useTableSort re-sorts whenever the columns identity changes, so
  // an inline array would re-sort on every render. The pending tab swaps the
  // two season columns for the application's own facts.
  const columns = useMemo<Column<Team>[]>(() => {
    const name: Column<Team> = {
      key: "name",
      header: "Název",
      primary: true,
      sortable: true,
      sortValue: (t) => t.name,
      render: (t) => {
        const leader = leaderName(t);
        return (
          <>
            <div>
              <RowLink to={`/teams/${t.id}`}>{t.name}</RowLink>
              {/* The ČNSMS ID is how the federation files a club, not how
                  anybody looks for one, so it trails the name quietly. */}
              {t.cmf_id != null && <Text variant="muted"> {t.cmf_id}</Text>}
            </div>
            {leader && <Text variant="muted">{leader}</Text>}
          </>
        );
      },
    };
    const federation: Column<Team> = {
      key: "federation",
      header: "Federace",
      sortable: true,
      sortValue: (t) => (t.federation_name ? federationLabel(labelOf(t)) : null),
      render: (t) => (
        <>
          <span title={t.federation_name}>
            {t.federation_name ? federationLabel(labelOf(t)) : "—"}
          </span>
          {/* The state the club represents is worth saying only where it
              is not the obvious one: every ČNSMS club is Czech. */}
          {!t.federation_is_primary && t.country_code && (
            <div>
              <Text variant="muted">{countryName(t.country_code)}</Text>
            </div>
          )}
        </>
      ),
    };
    const members: Column<Team> = {
      key: "members",
      header: "Počet členů",
      align: "right",
      sortable: true,
      sortValue: (t) => t.members_total ?? 0,
      render: (t) => t.members_total ?? 0,
    };
    const actions: Column<Team> = {
      key: "actions",
      header: "",
      render: (t) => {
        const missing = t.members_missing_cmf_id ?? 0;
        // Only ČNSMS issues ČNSMS IDs. Offering the button on a foreign team
        // would promise an allocation the server refuses.
        const canAssign =
          t.federation_is_primary &&
          t.kind === "full" &&
          t.status === "active" &&
          (t.cmf_id == null || missing > 0);
        return (
          <Table.Actions>
            <Table.Action appearance="button" to={`/teams/${t.id}`}>
              Detail
            </Table.Action>
            {!readsTeamOnly(me, t) && canAssign && (
              <Table.Action appearance="button" onClick={() => askAssignCmfId(t)}>
                Přidělit ČNSMS ID
              </Table.Action>
            )}
            {!readsTeamOnly(me, t) && t.status === "pending" && (
              <Table.Action appearance="button" onClick={() => askApprove(t)}>
                Schválit
              </Table.Action>
            )}
            {!readsTeamOnly(me, t) && t.status === "suspended" && (
              <Table.Action appearance="button" onClick={() => askApprove(t)}>
                Aktivovat
              </Table.Action>
            )}
            {!readsTeamOnly(me, t) && t.status === "active" && (
              <Table.Action appearance="button" variant="danger" onClick={() => setSuspending(t)}>
                Pozastavit
              </Table.Action>
            )}
          </Table.Actions>
        );
      },
    };

    if (pendingView) {
      return [
        name,
        federation,
        {
          key: "flow",
          header: "Žádost",
          sortable: true,
          sortValue: (t) => appliedFlowBadge(t.applied_flow)?.label ?? null,
          render: (t) => {
            // The door the club came through: both Czech applications
            // validate alike and write alike, so without it „chceme být
            // členy" and „chceme jen na pohárovky" would be approved blind.
            const flow = appliedFlowBadge(t.applied_flow);
            return flow ? <Badge variant={flow.variant}>{flow.label}</Badge> : <Text variant="muted">—</Text>;
          },
        },
        {
          key: "created",
          header: "Podáno",
          sortable: true,
          sortValue: (t) => t.created_at?.toISOString() ?? null,
          render: (t) => (t.created_at ? formatDate(t.created_at) : "—"),
        },
        members,
        actions,
      ];
    }
    return [
      name,
      federation,
      {
        key: "membership",
        header: seasonCode ? `Členství ${seasonCode}` : "Členství",
        sortable: true,
        sortValue: (t) => membershipBadge(t).label,
        render: (t) => {
          // With no active season the server computes no season state at
          // all, so an „aktivní" team would fall through to „Registrovaný".
          if (!seasonCode && viewOf(t) === "aktivni") return <Text variant="muted">—</Text>;
          const b = membershipBadge(t);
          return <Badge variant={b.variant}>{b.label}</Badge>;
        },
      },
      members,
      {
        key: "outstanding",
        header: "K dořešení",
        // Not sortable: the cell is a set of outstanding items, and there
        // is no order over „chybí foto" versus „nezaplaceno" that anyone
        // would predict from clicking the header.
        render: (t) => {
          // The server asks these questions of an active ČNSMS team alone
          // (fillState), so anywhere else an empty list means „not asked",
          // and „Vše hotovo" would be a verdict nothing reached. A team
          // owing its registration says so in „Členství" already.
          // A cup-only club is not registered, and „nothing outstanding"
          // would be a verdict on a season it is not in.
          if (
            !seasonCode ||
            !t.federation_is_primary ||
            viewOf(t) !== "aktivni" ||
            membershipOf(t) === "poharovky"
          ) {
            return <Text variant="muted">—</Text>;
          }
          const items = outstandingOf(t);
          if (items.length === 0) return <Text variant="muted">Vše hotovo</Text>;
          const missing = t.members_missing_cmf_id ?? 0;
          return (
            <BadgeRow>
              {items.map((k) => {
                if (k === "bez-cmf-id") {
                  return (
                    <Badge key={k} variant="warn">
                      {missing} bez ČNSMS ID
                    </Badge>
                  );
                }
                const title =
                  k === "chybi-foto" && (t.members_registered ?? 0) > 0
                    ? `${t.members_with_photo ?? 0}/${t.members_registered} má fotku`
                    : undefined;
                return (
                  <span key={k} title={title}>
                    <Badge variant="warn">{OUTSTANDING_LABEL[k]}</Badge>
                  </span>
                );
              })}
            </BadgeRow>
          );
        },
      },
      actions,
    ];
  }, [askApprove, askAssignCmfId, pendingView, seasonCode, me]);

  // Sorts the FILTERED rows: sorting the full list and filtering after
  // would be the same set, but the hook would also be re-sorting rows
  // nobody is looking at on every keystroke.
  const tableSort = useTableSort(rows, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "name", dir: "asc" },
  });

  const filtered = federationFilter !== "" || filtersInEffect(view, filters);

  return (
    <>
      <AppTopbar crumbs={[{ label: "Týmy" }]} />
      <Page>
        <Page.Header
          title="Týmy"
          subtitle={seasonCode ? `Sezóna ${seasonCode}` : undefined}
          actions={
            mayCreateTeam && (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Nový tým
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Tabs
            aria-label="Stav týmu"
            tabs={tabs}
            value={view}
            // The default tab keeps the URL clean, so /admin/tymy stays the
            // address of the list everybody means by it.
            onChange={(id) => setFilter("pohled", id === "aktivni" ? null : (id as TeamView))}
          />
          <FilterPanel>
            <FilterField wide>
              <Input
                label="Hledat"
                type="search"
                value={draft}
                placeholder="název týmu, vedoucí nebo ČNSMS ID"
                onChange={(e) => setDraft(e.target.value)}
              />
            </FilterField>
            {/* Offered only once there is something to choose between: with
                a single federation the control would name the one thing every
                row already shares. */}
            {federations.length > 1 && (
              <FilterField>
                <Select
                  label="Federace"
                  value={federationFilter}
                  onChange={(e) => setFilter("federation", e.target.value || null)}
                >
                  <option value="">Všechny</option>
                  {federations.map((f) => (
                    <option key={f.id} value={String(f.id)}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
            {view === "aktivni" && (
              <FilterField>
                <Select
                  label="Členství"
                  value={membership ?? ""}
                  onChange={(e) => setFilter("clenstvi", e.target.value || null)}
                >
                  <option value="">Všechna</option>
                  {MEMBERSHIP_OPTIONS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} ({membershipCounts[m.id]})
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
            {outstandingApplies(view) && (
              <FilterField>
                <Select
                  label="K dořešení"
                  value={outstanding ?? ""}
                  onChange={(e) => setFilter("k-doreseni", e.target.value || null)}
                >
                  <option value="">Cokoli</option>
                  {OUTSTANDING_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
          </FilterPanel>

          <Table<Team>
            framed
            {...tableSort.tableProps}
            columns={columns}
            rowKey={(t) => t.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            cards="sm"
            empty={
              // Different facts, different sentences: a filter that matched
              // nothing must not read as an empty tab, and neither as an
              // empty database.
              filtered ? (
                <EmptyState
                  title="Nic nenalezeno"
                  description={q ? `Hledání „${q}“ neodpovídá žádný tým.` : "Filtrům neodpovídá žádný tým."}
                />
              ) : teams.length === 0 ? (
                <EmptyState title="Žádné týmy" description="Zatím nebyl zaregistrován žádný tým." />
              ) : (
                <EmptyState
                  title={pendingView ? "Nic nečeká na schválení" : "Tady žádný tým není"}
                  description={`${pluralizeCzech(teams.length, ["Ostatní tým je", "Ostatní týmy jsou", "Ostatních týmů je"])} v jiných záložkách.`}
                />
              )
            }
          />
        </Page.Content>
      </Page>


      <FormModal
        open={creating}
        title="Nový tým"
        submitLabel="Vytvořit"
        busy={createTeam.isPending}
        canSubmit={
          createForm.name.trim().length > 0 &&
          createForm.country_code.length === 2 &&
          createForm.federation_id !== ""
        }
        error={createTeam.error}
        onClose={() => {
          setCreating(false);
          createTeam.reset();
        }}
        onSubmit={() =>
          createTeam.mutate(
            {
              name: createForm.name,
              country_code: createForm.country_code,
              federation_id: createForm.federation_id,
            },
            {
              onSuccess: () => {
                setCreating(false);
                setCreateForm({ name: "", country_code: "CZ", federation_id: "" });
              },
            },
          )
        }
      >
        <Input
          label="Název týmu"
          required
          value={createForm.name}
          onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
        />
        <Select
          label="Federace"
          required
          value={createForm.federation_id}
          onChange={(e) => setCreateForm({ ...createForm, federation_id: e.target.value })}
        >
          <option value="">Vyberte federaci</option>
          {federations.map((f: Federation) => (
            <option key={f.id} value={String(f.id)}>
              {f.name}
            </option>
          ))}
        </Select>
        <Select
          label="Stát"
          required
          value={createForm.country_code}
          onChange={(e) => setCreateForm({ ...createForm, country_code: e.target.value })}
          hint="Stát, který tým reprezentuje. Není to adresa na faktuře."
        >
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
        <p>
          Tým vzniká rovnou jako aktivní. Vedoucího, soupisku a fakturační údaje
          doplníte na jeho detailu.
        </p>
      </FormModal>


      <ConfirmDialog
        open={suspending !== null}
        title="Pozastavit tým"
        // Warning, not danger: „Aktivovat" puts the team straight back, and
        // red claims a permanence this act does not have.
        variant="warning"
        confirmLabel="Pozastavit"
        error={suspend.error}
        busy={suspend.isPending}
        onClose={closeSuspend}
        onConfirm={() => {
          if (!suspending) return;
          suspend.mutate({ id: suspending.id, reason }, { onSuccess: closeSuspend });
        }}
      >
        <p>
          Pozastavujete tým <strong>{suspending?.name}</strong>. Vedoucí ztratí
          přístup k údajům týmu.
        </p>
        <Input
          id="suspend-reason"
          label="Důvod (nepovinné)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </ConfirmDialog>

    </>
  );
}
